/**
 * Typed EventSource wrapper for Sharp8's SSE streams.
 *
 * Native EventSource handles browser reconnect via Last-Event-ID and
 * the standard retry algorithm. This wrapper adds:
 *
 *   · Visibility-aware lifecycle · closes the connection when the
 *     tab is hidden, reopens when it returns to the foreground.
 *     Saves battery + idle-proxy churn during long Telegram chat
 *     interruptions.
 *   · Telegram initData injection · for the /streams/me/picks
 *     stream, since EventSource can't send custom headers.
 *   · Soft fallback · if the stream fails to open at all (CORS,
 *     network, EventSource unsupported), the caller can choose to
 *     fall back to manual fetch via onError.
 */

import { tg } from './telegram';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export interface StreamHandle {
  close(): void;
}

export interface SubscribeOpts<T> {
  /** Relative path under /api/v1 (e.g. 'streams/me/picks' or 'streams/match/af-123'). */
  path: string;
  /** Event name to dispatch on; defaults to 'snapshot' (match stream) /
   *  'picks' (picks stream). Pass an array to listen on multiple. */
  events: string[];
  /** Whether to attach Telegram initData as ?initData= (picks stream
   *  needs it; match stream does not). */
  withInitData?: boolean;
  /** Extra query params merged into the URL. */
  query?: Record<string, string | undefined>;
  /** Called on every typed event; data is the parsed JSON payload. */
  onEvent(payload: T, eventName: string): void;
  /** Optional error callback — fires on transport-level error. The
   *  connection will auto-reconnect (browser default), but the caller
   *  may want to fall back to a one-shot fetch as a safety net. */
  onError?(err: Event): void;
}

export function subscribe<T>(opts: SubscribeOpts<T>): StreamHandle {
  let es: EventSource | null = null;
  let closed = false;

  const open = () => {
    if (closed || es) return;
    const initData = opts.withInitData ? (tg()?.initData ?? '') : '';
    const q = new URLSearchParams();
    if (initData) q.set('initData', initData);
    for (const [k, v] of Object.entries(opts.query ?? {})) {
      if (v != null) q.set(k, v);
    }
    const url = `${API_BASE.replace(/\/$/, '')}/${opts.path}${q.size > 0 ? `?${q.toString()}` : ''}`;
    try {
      es = new EventSource(url);
    } catch (err) {
      // Some Telegram WebViews (older Android) may throw constructor errors.
      opts.onError?.(new Event('eventsource_unsupported'));
      return;
    }
    for (const evt of opts.events) {
      es.addEventListener(evt, (ev) => {
        try {
          const payload = JSON.parse((ev as MessageEvent).data) as T;
          opts.onEvent(payload, evt);
        } catch {
          /* ignore malformed payload */
        }
      });
    }
    es.onerror = (e) => { opts.onError?.(e); };
  };

  const close = () => {
    if (es) {
      try { es.close(); } catch { /* ignore */ }
      es = null;
    }
  };

  // Open immediately if the tab is visible; otherwise wait.
  if (document.visibilityState === 'visible') open();

  const onVis = () => {
    if (document.visibilityState === 'visible') open();
    else                                         close();
  };
  document.addEventListener('visibilitychange', onVis);

  return {
    close() {
      closed = true;
      document.removeEventListener('visibilitychange', onVis);
      close();
    },
  };
}
